import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MouseEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Documents } from '../Documents/Documents';
import SocialEngagementTab from '../SocialEngagementTab/SocialEngagementTab';
import ClientSelectedHeader from './ClientSelectedHeader';

import ClientCalendarTab from './CalendarTab';
import CommunicationTab from './CommunicationTab';

import CustomCard from 'components/shared/CustomCard/CustomCard';
import ContactTab from 'components/shared/CustomDashboard/ContactsTab/ContactTab';
import DealsMain from 'components/featured/Deals';
import PrioritiesTab from './PrioritiesTab';
import ProfileDetailsTab from './ProfileDetailsTab';
import RateCardsTab from './RateCardsTab';
import RevenueTab from 'components/featured/Client/ClientSelected/RevenueTab';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import CustomHomepage from 'components/shared/CustomHomePage/CustomHomepage';
import CustomModal from 'components/shared/CustomModal/CustomModal';

import LinkingForm from 'pages/Dashboard/Client/ClientForm/Forms/LinkingForm';
import { ClientService } from 'service/client.service';
import { DashboardService } from 'service/dashboard.service';
import { HuddleClientService } from 'service/huddle-client.service';
import zeplinColor from 'theme/Color';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { clearToast } from 'store/slices/toastSlice';
import { Tasks } from 'pages/Dashboard/Tasks/Tasks';
import { usePermissions } from 'customHook/usePermissions';

type SportsProps = {
  link: '';
  title: '';
  source: '';
};

const ClientSelected = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: clientId } = useParams<{ id: string }>();
  const client = location.state?.client;
  const captive8IdStored = sessionStorage.getItem('captive8Id');
  const captive8UrlStored = sessionStorage.getItem('captive8Url');
  const currentTab = sessionStorage.getItem('currentTab') || '0';

  const [value, setValue] = useState(0);

  const toastMessage = useAppSelector((state) => state.toast.message);
  const [alert, setAlert] = useState('');

  useEffect(() => {
    if (toastMessage) {
      setAlert(toastMessage);
      dispatch(clearToast());
    }
  }, [toastMessage, dispatch]);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    sessionStorage.setItem('currentTab', newValue.toString());
    sessionStorage.removeItem('dealSelectedFromOverview');
    setValue(newValue);
  };

  const handleClose = () => {
    setAlert('');
  };

  const [data, setData] = useState<any>();
  const [details, setDetails] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [captive8loading, setCaptive8Loading] = useState(false);

  const [linkModal, setLinkModal] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [sportsData, setSportsData] = useState<SportsProps[]>([
    {
      link: '',
      title: '',
      source: '',
    },
  ]);

  const getDetails = async (id: string) => {
    setLoading(true);
    try {
      if (id) {
        const res = await HuddleClientService.getClientHuddleDetails(id);
        getCreatorInfo(res?.Captive8_Creator_Id__c || res?.captive8CreatorId);

        setDetails({
          accountName: res?.name,
          accountType: res?.recordType,
          name: res?.clientName || res?.name,
          ...res,
        });
      }
    } catch (err: any) {
      setAlert(err?.message as string);
    } finally {
      setLoading(false);
    }
  };

  const getDeal = async (id: string) => {
    try {
      const res = await HuddleClientService.getHuddleClientDeal(id);

      setData(res);
    } catch (err: any) {
      setAlert(err?.message as string);
    }
  };

  const getSportNews = async () => {
    try {
      const res: { latestNews: SportsProps[] } = await DashboardService.getSportNews();

      setSportsData(res?.latestNews);
    } catch (err: any) {
      setAlert(`sports api : ${err?.message as string}`);
    }
  };

  const getCreatorInfo = async (id: string) => {
    try {
      if (id) {
        setCaptive8Loading(true);
        const res = await ClientService.getCreatorInfo(id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCaptive8Loading(false);
    }
  };

  useEffect(() => {
    getDeal(clientId || '');
    getSportNews();
    getDetails(clientId || '');
    setValue(+currentTab || 0);
  }, [currentTab, clientId]);

  const { hasAction } = usePermissions();
  const canViewClientDeals = hasAction('Deals', 'AllDeals', 'read');
  const canViewClientTasks = hasAction('Tasks', 'AllTasks', 'read');

  const tabContentHeight = accordionExpanded
    ? { xs: 'calc(100vh - 460px)', xl: 'calc(100vh - 510px)' }
    : { xs: 'calc(100vh - 320px)', xl: 'calc(100vh - 370px)' };

  const DashboardTabs = [
    {
      label: 'Priorities',
      id: 1,
      isHidden: value !== 0,
      Component: (
        <Box>
          <PrioritiesTab accordionExpanded={accordionExpanded} />
        </Box>
      ),
    },
    {
      label: 'Profile Details',
      id: 2,
      isHidden: value !== 1,
      Component: (
        <Box>
          <ProfileDetailsTab
            details={details}
            loading={loading}
            accordionExpanded={accordionExpanded}
          />
        </Box>
      ),
    },
    {
      label: 'Deals',
      id: 3,
      isHidden: value !== 2 || !canViewClientDeals,
      Component: (
        <Box sx={{ height: tabContentHeight, overflowY: 'auto' }}>
          <DealsMain
            clientScope={{
              clientId: details?.accountId || details?.clientId || clientId || '',
              clientName: details?.name || details?.accountName || client?.Name || '',
            }}
          />
        </Box>
      ),
    },
    {
      label: 'Task',
      id: 4,
      isHidden: value !== 3 || !canViewClientTasks,
      Component: (
        <Box sx={{ height: tabContentHeight, overflowY: 'auto' }}>
          <Tasks
            clientScope={{
              clientId: details?.accountId || details?.clientId || clientId || '',
              clientName: details?.name || details?.accountName || client?.Name || '',
            }}
          />
        </Box>
      ),
    },
    {
      label: 'Communication',
      id: 5,
      isHidden: value !== 4,
      Component: (
        <Box sx={{ height: tabContentHeight, overflowY: 'auto' }}>
          <CommunicationTab />
        </Box>
      ),
    },
    {
      label: 'Revenue',
      id: 6,
      isHidden: value !== 5,
      Component: (
        <Box sx={{ height: tabContentHeight, overflowY: 'auto' }}>
          <RevenueTab />
        </Box>
      ),
    },
    {
      label: 'Social',
      id: 7,
      isHidden: value !== 6,
      Component: (
        <Box sx={{ height: tabContentHeight, overflowY: 'auto' }}>
          <SocialEngagementTab
            key={details?.Captive8_Creator_Id__c || details?.captive8Id || captive8IdStored}
            captive8Id={details?.Captive8_Creator_Id__c || details?.captive8Id || captive8IdStored}
            captive8Url={details?.captive8Url || captive8UrlStored}
            getDetails={() => {
              getDetails(clientId || '');
            }}
          />
        </Box>
      ),
    },
    {
      label: 'Contacts',
      id: 8,
      isHidden: value !== 7,
      Component: (
        <Box sx={{ height: tabContentHeight, overflowY: 'auto' }}>
          <ContactTab />
        </Box>
      ),
    },
    {
      label: 'Documents',
      id: 9,
      isHidden: value !== 8,
      Component: (
        <Box sx={{ height: '100%' }}>
          <Documents />
        </Box>
      ),
    },
    {
      label: 'Calendar',
      id: 10,
      isHidden: value !== 9,
      Component: (
        <Box sx={{ height: tabContentHeight, overflowY: 'auto' }}>
          <ClientCalendarTab />
        </Box>
      ),
    },
    // {
    //   label: 'Rate Cards',
    //   id: 11,
    //   isHidden: value !== 10,
    //   Component: (
    //     <Box sx={{ height: '100%' }}>
    //       <RateCardsTab />
    //     </Box>
    //   ),
    // },
  ];

  const handleDeleteClick = (event: MouseEvent<HTMLDivElement>) => {
    setOpenModal((prev) => !prev);
  };

  const handleDeleteConfirm = async (id: string) => {
    try {
      await HuddleClientService.deleteHuddleClient(id);
      navigate('/clients');
    } catch (err) {
      setAlert('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHomepage
        sportsData={sportsData}
        customHeader={
          <ClientSelectedHeader
            details={details}
            captive8loading={captive8loading}
            menuAnchor={menuAnchor}
            menuOpen={menuOpen}
            setMenuAnchor={setMenuAnchor}
            onEdit={() => navigate(`/clients/${clientId}/edit`)}
            handleDeleteClick={handleDeleteClick}
            accordionExpanded={accordionExpanded}
            setAccordionExpanded={setAccordionExpanded}
          />
        }
        DashboardTabs={DashboardTabs}
        handleChange={handleChange}
        value={value}
        alert={alert}
        handleClose={handleClose}
        basicTab={true}
      />

      <CustomModal handleClose={() => setOpenModal(false)} open={openModal}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: { xs: '20px', xl: '24px' },
              backgroundColor: zeplinColor.Neutral1,
              boxShadow: 'rgba(0, 0, 0, 0.15)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '22px', xl: '32px' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Archive Client?
                </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  You’re about to archive{' '}
                  <Typography variant='responsiveHeading4'>{details?.accountName}</Typography>
                  <br /> and all related data. This action can’t be
                  <br /> undone. Are you sure you want to proceed?
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: '10px', xl: '16px' },
                }}
              >
                <Box
                  onClick={() => {
                    setOpenModal(false);
                  }}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                    aria-label='button'
                    aria-disabled={loading}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box
                  onClick={() => {
                    handleDeleteConfirm(details?.clientId ?? details?.accountId ?? clientId ?? '');
                  }}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Error,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                    }}
                    aria-label='button'
                    aria-disabled={loading}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      {loading ? 'Archiving...' : 'Yes, Archive'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
        </Box>
      </CustomModal>
      <LinkingForm
        modalOpen={linkModal}
        key={linkModal?.toString()}
        setLinkModal={setLinkModal}
        clientId={client?.AccountId}
        client={details || client}
        getAllClient={() => {
          getDetails(clientId || '');
        }}
      />
    </>
  );
};

export default ClientSelected;
